import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Route, Routes, useLocation } from 'react-router-dom';

import { Box } from '@mui/material';

import LeaderboardIcon from '@mui/icons-material/Leaderboard';

import { ReactComponent as AccountIcon } from '../../../assets/icons/account.svg';
import { ReactComponent as AgreementsIcon } from '../../../assets/icons/agreements/agreements.svg';
import { ReactComponent as NotificationIcon } from '../../../assets/icons/notifications/notification.svg';
import { ReactComponent as SecurityIcon } from '../../../assets/icons/security.svg';
import { ReactComponent as SettingsIcon } from '../../../assets/icons/settings.svg';
import { ReactComponent as FunctionalityIcon } from '../../../assets/icons/settings/functionality.svg';
import AppVerticalRouteMenu, {
  IVerticalRouteMenuItemConfig,
} from '../../../components/designe/app-vertical-route-menu/AppVerticalRouteMenu';
import PageContainer from '../../../components/layout/page-container/PageContainer';
import AppNavigate from '../../../components/reusable/app-navigate/AppNavigate';
import { Paths } from '../../../shared/paths';
import { useStyles } from './Settings.styles';
import AccessSettings from './access-settings/AccessSettings';
import FunctionalityEdit from './functionality-edit/FunctionalityEdit';
import FunctionalityView from './functionality-view/FunctionalityView';
import { LinkedAccounts } from './linked-accounts/LinkedAccounts';
import MeasurementsView from './measurements/MeasurementsView';
import NotificationsEdit from './notifications-edit/NotificationsEdit';
import NotificationsView from './notifications-view/NotificationsView';
import UserConfigEdit from './user-config-edit/UserConfigEdit';
import UserConfigView from './user-config-view/UserConfigView';
import UserInfoEdit from './user-info-edit/UserInfoEdit';
import UserInfoView from './user-info-view/UserInfoView';

const settingsInfoPath = Paths.settings.info.replace('/', '');
const settingsConfigPath = Paths.settings.config.replace('/', '');
const settingsNotificationsPath = Paths.settings.notifications.replace('/', '');
const settingsFunctionalityPath = Paths.settings.functionality.replace('/', '');
const settingsVisualizationPath = Paths.settings.visualization.replace('/', '');
const settingsLinkedAccountsPath = Paths.settings.linkedAccounts.replace(
  '/',
  '',
);
const settingsMeasurementsPath = Paths.settings.measurements.replace('/', '');
const settingsAccessPath = Paths.settings.access.replace('/', '');
const settingsViewPath = Paths.settings.view.replace('/', '');
const settingsEditPath = Paths.settings.edit.replace('/', '');

function RedirectVisualizationToFunctionality() {
  const location = useLocation();
  const to = location.pathname.endsWith(`${Paths.settings.edit}`)
    ? Paths.settings.toFunctionalityEdit
    : Paths.settings.toFunctionalityView;
  return <AppNavigate to={to} replace />;
}

interface ISettingsSectionLayoutProps {
  children: React.ReactNode;
}

function SettingsSectionLayout({ children }: ISettingsSectionLayoutProps) {
  const { classes } = useStyles();

  return <Box className={classes.sectionContentContainer}>{children}</Box>;
}

function InfoSettingsRoutes() {
  return (
    <Routes>
      <Route
        path={settingsViewPath}
        element={
          <SettingsSectionLayout>
            <UserInfoView />
          </SettingsSectionLayout>
        }
      />
      <Route
        path={settingsEditPath}
        element={
          <SettingsSectionLayout>
            <UserInfoEdit />
          </SettingsSectionLayout>
        }
      />
      <Route
        path={'*'}
        element={<AppNavigate to={Paths.settings.toInfoView} replace />}
      />
    </Routes>
  );
}

function ConfigSettingsRoutes() {
  return (
    <Routes>
      <Route
        path={settingsViewPath}
        element={
          <SettingsSectionLayout>
            <UserConfigView />
          </SettingsSectionLayout>
        }
      />
      <Route
        path={settingsEditPath}
        element={
          <SettingsSectionLayout>
            <UserConfigEdit />
          </SettingsSectionLayout>
        }
      />
      <Route
        path={'*'}
        element={<AppNavigate to={Paths.settings.toConfigView} replace />}
      />
    </Routes>
  );
}

function NotificationsSettingsRoutes() {
  return (
    <Routes>
      <Route
        path={settingsViewPath}
        element={
          <SettingsSectionLayout>
            <NotificationsView />
          </SettingsSectionLayout>
        }
      />
      <Route
        path={settingsEditPath}
        element={
          <SettingsSectionLayout>
            <NotificationsEdit />
          </SettingsSectionLayout>
        }
      />
      <Route
        path={'*'}
        element={
          <AppNavigate to={Paths.settings.toNotificationsView} replace />
        }
      />
    </Routes>
  );
}

function FunctionalitySettingsRoutes() {
  return (
    <Routes>
      <Route
        path={settingsViewPath}
        element={
          <SettingsSectionLayout>
            <FunctionalityView />
          </SettingsSectionLayout>
        }
      />
      <Route
        path={settingsEditPath}
        element={
          <SettingsSectionLayout>
            <FunctionalityEdit />
          </SettingsSectionLayout>
        }
      />
      <Route
        path={'*'}
        element={
          <AppNavigate to={Paths.settings.toFunctionalityView} replace />
        }
      />
    </Routes>
  );
}

function Settings() {
  const { t, i18n } = useTranslation();
  const { classes } = useStyles();
  const location = useLocation();
  const isSettingsRootRoute =
    location.pathname === Paths.settings.root ||
    location.pathname === `${Paths.settings.root}/`;
  const menuConfig = useMemo(() => {
    const config: IVerticalRouteMenuItemConfig[] = [
      {
        label: t('settings.userInfo'),
        path: Paths.settings.toInfoView,
        additionalPaths: [Paths.settings.toInfoEdit],
        icon: AccountIcon,
      },
      {
        label: t('settings.measurements.tab'),
        path: Paths.settings.toMeasurements,
        icon: () => <LeaderboardIcon />,
      },
      {
        label: t('settings.userSettings'),
        path: Paths.settings.toConfigView,
        additionalPaths: [Paths.settings.toConfigEdit],
        icon: SettingsIcon,
      },
      {
        label: t('settings.notificationsTab'),
        path: Paths.settings.toNotificationsView,
        additionalPaths: [Paths.settings.toNotificationsEdit],
        icon: NotificationIcon,
      },
      {
        label: t('settings.functionality.tab'),
        path: Paths.settings.toFunctionalityView,
        additionalPaths: [Paths.settings.toFunctionalityEdit],
        icon: FunctionalityIcon,
      },
      {
        label: t('settings.access'),
        path: Paths.settings.toAccess,
        icon: SecurityIcon,
      },
      {
        label: t('settings.linkedAccounts'),
        path: Paths.settings.toLinkedAccounts,
        icon: AgreementsIcon,
      },
    ];

    return config;
  }, [i18n.language, t]);

  return (
    <PageContainer
      title={t('settings.settings')}
      backButton={!isSettingsRootRoute}>
      <Routes>
        <Route
          index
          element={
            <div className={classes.menuPageContainer}>
              <AppVerticalRouteMenu config={menuConfig} />
            </div>
          }
        />
        <Route
          path={`${settingsInfoPath}/*`}
          element={<InfoSettingsRoutes />}
        />
        <Route
          path={`${settingsConfigPath}/*`}
          element={<ConfigSettingsRoutes />}
        />
        <Route
          path={`${settingsNotificationsPath}/*`}
          element={<NotificationsSettingsRoutes />}
        />
        <Route
          path={`${settingsFunctionalityPath}/*`}
          element={<FunctionalitySettingsRoutes />}
        />
        <Route
          path={`${settingsVisualizationPath}/*`}
          element={<RedirectVisualizationToFunctionality />}
        />
        <Route
          path={settingsLinkedAccountsPath}
          element={
            <SettingsSectionLayout>
              <LinkedAccounts />
            </SettingsSectionLayout>
          }
        />
        <Route
          path={settingsMeasurementsPath}
          element={
            <SettingsSectionLayout>
              <MeasurementsView />
            </SettingsSectionLayout>
          }
        />
        <Route
          path={settingsAccessPath}
          element={
            <SettingsSectionLayout>
              <AccessSettings />
            </SettingsSectionLayout>
          }
        />
        <Route
          path={'*'}
          element={<AppNavigate to={Paths.settings.toInfoView} replace />}
        />
      </Routes>
    </PageContainer>
  );
}

export default Settings;
